import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useConfirmation } from '@/components/ui/useConfirmation'
import { cn } from '@/lib/utils'
import { WixIcon } from './ProviderIcons'
import {
  isConnectUnavailable,
  useConnectCalendar,
  useUpdateWixBooking,
  useWixOptions,
  type ConnectedCalendar,
  type WixService,
} from '@/api/calendars'

interface Props {
  calendar: ConnectedCalendar | null
  onClose: () => void
  companyId: number | undefined
}

const ALL_SERVICES = '__all_services__'
const ANYONE = '__anyone__'

/** "Haircut · 30 min · 40 CAD", the way a service reads in the lists. */
function serviceLabel(s: WixService) {
  return `${s.name} · ${s.duration_minutes} min${s.price != null ? ` · ${s.price} ${s.currency ?? ''}` : ''}`
}

/**
 * What the agent books on the Wix site: one service or all of them, and
 * optionally one team member. The lists come from the Wix site.
 */
export function WixSettingsDialog({ calendar, onClose, companyId }: Props) {
  const { t } = useTranslation()
  const appRemoved = calendar?.wix?.last_error === 'wix_app_removed'
  const [service, setService] = useState('')
  const [member, setMember] = useState(ANYONE)
  // No point asking Wix while the app is removed from the site: it would refuse.
  const options = useWixOptions(companyId, calendar && !appRemoved ? calendar.id : null)
  const update = useUpdateWixBooking(companyId)

  useEffect(() => {
    if (!calendar?.wix) return
    setService(calendar.wix.service_id || ALL_SERVICES)
    setMember(calendar.wix.staff_resource_id || ANYONE)
  }, [calendar])

  const services = options.data?.services ?? []
  const staff = options.data?.staff ?? []
  const servicesOfMember =
    member === ANYONE
      ? services
      : services.filter((s) => s.staff.length === 0 || s.staff.some((m) => m.id === member))
  // A single service: "all services" means that one.
  const chosen = service === ALL_SERVICES && services.length === 1 ? services[0].id : service

  const changeMember = (next: string) => {
    setMember(next)
    const offered =
      next === ANYONE || services.some((s) => s.id === chosen && (s.staff.length === 0 || s.staff.some((m) => m.id === next)))
    if (chosen !== ALL_SERVICES && !offered) setService(ALL_SERVICES)
  }

  const save = async () => {
    if (!calendar || !chosen) return
    try {
      await update.mutateAsync({
        calendarId: calendar.id,
        service_id: chosen === ALL_SERVICES ? '' : chosen,
        staff_resource_id: member === ANYONE ? '' : member,
        is_active: true,
      })
      toast.success(t('manager.calendars.wix.saved'))
      onClose()
    } catch (e) {
      const code = (e as { response?: { data?: { error?: string } } })?.response?.data?.error
      toast.error(t('manager.calendars.wix.saveError'), {
        description: code === 'wix_error' ? t('manager.calendars.wix.errors.wix_error') : code,
      })
    }
  }

  const lastError = calendar?.wix?.last_error

  return (
    <Dialog open={!!calendar} onOpenChange={(o) => !o && onClose()}>
      <DialogContent showCloseButton={false} className="sm:max-w-md overflow-hidden border-0 p-0 shadow-2xl">
        <div className="h-1 w-full bg-gradient-to-r from-blue-600 via-blue-400 to-blue-600" />
        <div className="p-6 pb-0">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white dark:bg-slate-800 shadow-sm ring-1 ring-black/5 dark:ring-white/10">
              <WixIcon className="w-6 h-6" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-xl tracking-tight">
                {t('manager.calendars.wix.title')}
              </DialogTitle>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {t('manager.calendars.wix.subtitle')}
              </p>
              {calendar?.wix?.business_name && (
                <p className="mt-0.5 text-xs text-slate-400 truncate">{calendar.wix.business_name}</p>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4 px-6 pt-4 pb-2">
          {appRemoved && calendar ? (
            <div className="space-y-3">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                {t('manager.calendars.wix.errors.wix_app_removed')}
              </p>
              <ReconnectWixButton calendar={calendar} companyId={companyId} />
            </div>
          ) : options.isLoading ? (
            <div className="py-6 text-center">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />
            </div>
          ) : options.isError ? (
            <p className="text-sm text-red-600">{t('manager.calendars.wix.loadError')}</p>
          ) : (
            <>
              {lastError && (
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  {t(`manager.calendars.wix.errors.${lastError}`, t('manager.calendars.wix.errors.wix_error'))}
                </p>
              )}

              <div>
                <Label className="text-sm font-medium">{t('manager.calendars.wix.service')}</Label>
                <Select value={chosen} onValueChange={setService}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder={t('manager.calendars.wix.pickService')} />
                  </SelectTrigger>
                  <SelectContent>
                    {services.length > 1 && (
                      <SelectItem value={ALL_SERVICES}>
                        {t('manager.calendars.wix.allServices', { count: services.length })}
                      </SelectItem>
                    )}
                    {servicesOfMember.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {serviceLabel(s)}
                        {s.approbation && (
                          <span className="text-slate-400"> · {t('manager.calendars.wix.needsApproval')}</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1.5">
                  {services.length === 0
                    ? t('manager.calendars.wix.noService')
                    : chosen === ALL_SERVICES
                      ? t('manager.calendars.wix.allServicesHelp')
                      : t('manager.calendars.wix.oneServiceHelp')}
                </p>
              </div>

              {staff.length > 0 && (
                <div>
                  <Label className="text-sm font-medium">{t('manager.calendars.wix.member')}</Label>
                  <Select value={member} onValueChange={changeMember}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ANYONE}>{t('manager.calendars.wix.anyMember')}</SelectItem>
                      {staff.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name || t('manager.calendars.wix.unnamedMember')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t bg-slate-50 dark:bg-slate-800/50 px-6 py-4 mt-4">
          <Button type="button" variant="ghost" onClick={onClose} disabled={update.isPending}>
            {t('manager.calendars.modal.cancel')}
          </Button>
          {!appRemoved && (
            <Button
              type="button"
              onClick={() => void save()}
              disabled={update.isPending || !chosen || services.length === 0 || options.isLoading || options.isError}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            >
              {update.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('manager.calendars.wix.save')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Sends the client back to Wix to add the Callizy app to the site again.
 * The server finds the same calendar by its site, so agents keep it.
 */
export function ReconnectWixButton({
  calendar,
  companyId,
  warnUnsaved = false,
  className,
}: {
  calendar: ConnectedCalendar
  companyId: number | undefined
  /** Inside a form with unsaved changes: the full page redirect would lose them. */
  warnUnsaved?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const connect = useConnectCalendar(companyId)
  const { demander, dialogue } = useConfirmation()

  const reconnect = async () => {
    if (
      warnUnsaved &&
      !(await demander({
        titre: t('manager.calendars.wix.leaveTitle'),
        texte: t('manager.calendars.modal.unsavedWarning'),
        action: t('manager.calendars.wix.leaveAction'),
      }))
    ) {
      return
    }
    try {
      const url = await connect.mutateAsync({ provider: 'wix', calendar_name: calendar.calendar_name })
      window.location.href = url
    } catch (e) {
      toast.error(
        isConnectUnavailable(e)
          ? t('manager.calendars.wix.unavailable')
          : t('manager.calendars.toast.connectError'),
      )
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => void reconnect()}
        disabled={connect.isPending}
        className={cn('gap-2', className)}
      >
        {connect.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <WixIcon className="h-4 w-4" />}
        {t('manager.calendars.wix.reconnect')}
      </Button>
      {dialogue}
    </>
  )
}
